import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faSpinner, 
  faTimes, 
  faCheckCircle, 
  faExclamationTriangle,
  faDownload,
  faFileExcel,
  faTree
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import * as ExcelJS from 'exceljs';
import { ECRCode, ECRImportRow } from '../../types/ecrCode';

interface ECRImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (codes: ECRCode[]) => void;
}

const ECRImportModal: React.FC<ECRImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onImportComplete 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ECRCode[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const buildECRHierarchy = (rows: ECRImportRow[]): ECRCode[] => {
    const codesMap = new Map<string, ECRCode>();
    const rootCodes: ECRCode[] = [];

    // Erste Phase: Alle Codes erstellen
    rows.forEach(row => {
      const code: ECRCode = {
        value: row._Value,
        parent: row._parent || undefined,
        description: row.WEDLECRBEZ_DE?.trim() || '',
        level1Code: row.WEDLECRECRSGECRSUGECRWGREbene1,
        level2Code: row.WEDLECRECRSGECRSUGECRWGREbene2 || undefined,
        fullCode: row._Value,
        level: row._parent ? (row._parent.length === 2 ? 2 : 3) : 1,
        children: []
      };
      codesMap.set(row._Value, code);
    });

    // Zweite Phase: Hierarchie aufbauen
    codesMap.forEach(code => {
      if (code.parent) {
        const parentCode = codesMap.get(code.parent);
        if (parentCode) {
          parentCode.children = parentCode.children || [];
          parentCode.children.push(code);
        }
      } else {
        rootCodes.push(code);
      }
    });

    return rootCodes;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setShowPreview(false);
    }
  };

  const processExcelFile = useCallback(async (file: File): Promise<ECRCode[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const buffer = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
          const worksheet = workbook.worksheets[0];
          
          // Convert worksheet to JSON data
          const jsonData: any[][] = [];
          worksheet.eachRow((row, rowNumber) => {
            const rowData: any[] = [];
            row.eachCell((cell, colNumber) => {
              rowData[colNumber - 1] = cell.value || '';
            });
            jsonData.push(rowData);
          });

          if (jsonData.length < 2) {
            throw new Error('Excel-Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten');
          }

          const headers = jsonData[0] as string[];
          const expectedHeaders = ['_Value', '_parent', 'WEDLECRBEZ_DE', 'WEDLECRECRSGECRSUGECRWGREbene1', 'WEDLECRECRSGECRSUGECRWGREbene2'];
          
          const missingHeaders = expectedHeaders.filter(header => 
            !headers.some(h => h.includes(header.replace('WEDLECRBEZ_DE', 'WEDLECRBEZ_DE').replace('WEDLECRECRSGECRSUGECRWGR', 'WEDLECRECRSGECRSUGECRWGR')))
          );

          if (missingHeaders.length > 0) {
            console.warn('Fehlende Header:', missingHeaders);
          }

          const rows: ECRImportRow[] = jsonData.slice(1).map((row: any[]) => ({
            _Value: String(row[0] || '').trim(),
            _parent: String(row[1] || '').trim(),
            WEDLECRBEZ_DE: String(row[2] || '').trim(),
            WEDLECRECRSGECRSUGECRWGREbene1: String(row[3] || '').trim(),
            WEDLECRECRSGECRSUGECRWGREbene2: String(row[4] || '').trim()
          })).filter(row => row._Value && row.WEDLECRBEZ_DE);

          if (rows.length === 0) {
            throw new Error('Keine gültigen ECR-Code-Daten in der Excel-Datei gefunden');
          }

          const hierarchy = buildECRHierarchy(rows);
          resolve(hierarchy);
        } catch (error) {
          reject(new Error(`Fehler beim Verarbeiten der Excel-Datei: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`));
        }
      };
      reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const handlePreview = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);

    try {
      const codes = await processExcelFile(file);
      setPreviewData(codes);
      setShowPreview(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Verarbeiten der Datei');
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);

    try {
      const codes = showPreview ? previewData : await processExcelFile(file);
      onImportComplete(codes);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Importieren');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    const templateData = [
      ['_Value', '_parent', 'WEDLECRBEZ_DE', 'WEDLECRECRSGECRSUGECRWGREbene1', 'WEDLECRECRSGECRSUGECRWGREbene2'],
      ['00', '', 'Obst & Gemüse', '00', ''],
      ['0001', '00', 'Obst (frisch)', '00', '0001'],
      ['0002', '00', 'Gemüse (frisch) inkl. Kräuter und Pilze', '00', '0002'],
      ['000126000', '0001', 'Zitrusfrüchte per Stk', '00', '0001'],
      ['000126001', '0001', 'Zitrusfrüchte per kg', '00', '0001'],
      ['000126100', '0001', 'Steinobst per kg', '00', '0001']
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('ECR Codes');
    
    // Add all template data rows
    templateData.forEach(row => {
      worksheet.addRow(row);
    });
    
    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ECR_Codes_Template.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const renderECRTree = (codes: ECRCode[], level = 0) => {
    return codes.map(code => (
      <div key={code.value} className={`ml-${level * 4} mb-1`}>
        <div className={`flex items-center gap-2 p-2 rounded ${
          level === 0 ? 'bg-blue-50 border-l-4 border-blue-500' :
          level === 1 ? 'bg-green-50 border-l-4 border-green-500' :
          'bg-gray-50 border-l-4 border-gray-500'
        }`}>
          <span className="font-mono text-sm font-bold text-gray-700">
            {code.value}
          </span>
          <span className="text-sm text-gray-900">
            {code.description}
          </span>
          {code.children && code.children.length > 0 && (
            <span className="text-xs text-gray-500">
              ({code.children.length} Unterkategorien)
            </span>
          )}
        </div>
        {code.children && code.children.length > 0 && (
          <div className="mt-1">
            {renderECRTree(code.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faTree} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">ECR-Codes Import</h3>
              <p className="text-sm text-gray-600">Hierarchische ECR-Warencodes aus Excel importieren</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {!showPreview ? (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon icon={faDownload} className="text-blue-600 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-1">Excel-Vorlage herunterladen</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Laden Sie die Vorlage herunter, um das richtige Format für den ECR-Code-Import zu sehen.
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      <FontAwesomeIcon icon={faFileExcel} />
                      Vorlage herunterladen
                    </button>
                  </div>
                </div>
              </div>

              {/* File Upload */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Excel-Datei auswählen
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faUpload} className="text-gray-400 text-2xl" />
                      <span className="text-sm text-gray-600">
                        Klicken Sie hier oder ziehen Sie eine Excel-Datei hierher
                      </span>
                      <span className="text-xs text-gray-500">
                        Unterstützte Formate: .xlsx, .xls
                      </span>
                    </label>
                  </div>
                  {file && (
                    <div className="mt-2 text-sm text-gray-600">
                      Ausgewählte Datei: <span className="font-medium">{file.name}</span>
                    </div>
                  )}
                </div>

                {/* Required Columns Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Erwartete Spalten:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div><code className="bg-white px-2 py-1 rounded">_Value</code> - ECR-Code</div>
                    <div><code className="bg-white px-2 py-1 rounded">_parent</code> - Übergeordneter Code</div>
                    <div><code className="bg-white px-2 py-1 rounded">WEDLECRBEZ_DE</code> - Bezeichnung</div>
                    <div><code className="bg-white px-2 py-1 rounded">WEDLECRECRSGECRSUGECRWGREbene1</code> - Ebene 1</div>
                    <div><code className="bg-white px-2 py-1 rounded">WEDLECRECRSGECRSUGECRWGREbene2</code> - Ebene 2</div>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-900 mb-1">Importfehler</h4>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  Vorschau - {previewData.length} Hauptkategorien gefunden
                </h4>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Zurück zur Dateiauswahl
                </button>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
                {renderECRTree(previewData)}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-1" />
                <div className="flex-1">
                  <h4 className="font-medium text-green-900 mb-1">Import bereit</h4>
                  <p className="text-sm text-green-700">
                    Die ECR-Codes wurden erfolgreich verarbeitet und können jetzt importiert werden.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={importing}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          
          {!showPreview ? (
            <button
              onClick={handlePreview}
              disabled={!file || importing}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {importing && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
              Vorschau
            </button>
          ) : (
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {importing && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
              Importieren
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ECRImportModal;
